import React, { useState, useEffect, useRef } from 'react'
import { Button, Collapse, Alert } from '@mui/material'
import { keyframes } from '@mui/material/styles'
import SyncIcon from '@mui/icons-material/Sync'
import { apiUrl, wsUrl } from '../../environments/environments'
import { ApiService } from '@carrier-ecat/workflowui-globalfunctions'

const spin = {
    '0%': {
        transform: 'rotate(0deg)',
    },
    '100%': {
        transform: 'rotate(360deg)',
    },
}

const spinAnimation = keyframes(spin)

const SyncPriceButton = ({
    builder,
    product,
    onSyncComplete,
    onError,
    initialMessage = 'Sync Prices',
    buttonStyle = {},
    buttonProps = {},
    className = '',
    showInlineError = true,
    syncUrl,
    syncMethod = 'get',
    syncParams,
}) => {
    const [syncJobId, setSyncJobId] = useState(null)
    const [syncing, setSyncing] = useState(false)
    const [progressMessage, setProgressMessage] = useState('Syncing...')
    const [errorMessage, setErrorMessage] = useState('')
    const wsRef = useRef(null)

    const clearError = () => {
        setErrorMessage('')
        if (onError) {
            onError('')
        }
    }

    const reportError = (message, rawError = null) => {
        setErrorMessage(message)
        if (onError) {
            onError(message, rawError)
        }
    }

    // Check for existing job on mount
    useEffect(() => {
        const storedJobId = localStorage.getItem(`${builder}-${product || 'all'}-syncId`)
        if (storedJobId) {
            setSyncJobId(storedJobId)
            setSyncing(true)
        }
    }, [builder, product])

    // Handle WebSocket connection when jobId changes
    useEffect(() => {
        if (syncJobId && syncing) {
            connectWebSocket(syncJobId)
        }

        return () => {
            if (wsRef.current) {
                wsRef.current.close()
                wsRef.current = null
            }
        }
    }, [syncJobId, syncing])

    const connectWebSocket = (jobId) => {
        try {
            const token = localStorage.getItem('msal.idtoken') || ''
            const wsConnection = new WebSocket(`${wsUrl}?token=${token}`)
            wsRef.current = wsConnection

            wsConnection.onopen = () => {
                wsConnection.send(JSON.stringify({ jobId }))
            }

            wsConnection.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data)
                    setProgressMessage(data.status || 'Processing...')

                    if (data.error) {
                        reportError(data.error, data)
                    }

                    if (data.completed) {
                        setSyncing(false)
                        localStorage.removeItem(`${builder}-${product || 'all'}-syncId`)
                        setSyncJobId(null)

                        if (!data.error) {
                            clearError()
                            if (onSyncComplete) {
                                onSyncComplete()
                            }
                        }

                        if (wsRef.current) {
                            wsRef.current.close()
                            wsRef.current = null
                        }
                    }
                } catch (error) {
                    console.error('Error parsing WebSocket message:', error)
                }
            }

            wsConnection.onerror = (error) => {
                console.error('WebSocket error:', error)
                reportError('WebSocket connection error. Please try again later.', error)
                setSyncing(false)
            }

            wsConnection.onclose = () => {}
        } catch (error) {
            console.error('Error creating WebSocket:', error)
            reportError('Failed to establish WebSocket connection.', error)
            setSyncing(false)
        }
    }

    const syncPrices = async () => {
        try {
            setSyncing(true)
            setProgressMessage('Starting...')
            clearError()

            const requestBody = syncMethod.toLowerCase() === 'post' ? {} : null

            const { data: responseData } = await ApiService(
                syncUrl || `${apiUrl}/SAPPrices/SynchronizePricesWithSap`,
                syncMethod,
                requestBody,
                null,
                0,
                syncParams || {
                    builder: builder,
                    product: product,
                }
            )

            const { jobId } = responseData

            if (!jobId) {
                reportError('No jobId received from server', responseData)
                setSyncing(false)
                return
            }

            localStorage.setItem(`${builder}-${product || 'all'}-syncId`, jobId)
            setSyncJobId(jobId)
        } catch (error) {
            console.error('Error syncing prices:', error)
            reportError(error.response?.data?.error || error.message || 'Failed to start price synchronization.', error)
            setSyncing(false)
        }
    }

    return (
        <>
            <Button
                id={'sync_prices'}
                color='primary'
                variant='contained'
                size='large'
                className={className}
                style={buttonStyle}
                startIcon={<SyncIcon sx={syncing ? { animation: `${spinAnimation} 2s linear infinite` } : {}} />}
                onClick={syncPrices}
                disabled={syncing}
                {...buttonProps}>
                {syncing ? progressMessage || 'Syncing...' : initialMessage}
            </Button>
            {showInlineError && (
                <Collapse in={!!errorMessage}>
                    <Alert severity='error' onClose={clearError} sx={{ mt: 2 }}>
                        {errorMessage}
                    </Alert>
                </Collapse>
            )}
        </>
    )
}

export default SyncPriceButton
